import {
  DetectionModel,
  DetectionModelName,
  DropdownOption,
  ExternalUserFieldAccess,
  FormField,
  getNumberFieldByDataKey,
  getStringFieldByDataKey,
  InputType,
} from 'types'
import { optionalNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export interface BasicThresholdDetectionModelFormData {
  modelName: string
  isEnabled: string
  lowerThreshold?: number
  lowerLowerThreshold?: number
  upperThreshold?: number
  upperUpperThreshold?: number
}

export type DetectionModelFormData = BasicThresholdDetectionModelFormData

const modelNameOptions: DropdownOption[] = [
  { label: 'Basic Threshold', value: DetectionModelName.BASIC_THRESHOLD },
]

const enabledOptions: DropdownOption[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

export const DetectionModelFormFields = ({
  data,
}: {
  data: DetectionModelFormData
}): FormField[] => {
  return [
    {
      dataKey: 'modelName',
      label: 'Model Type',
      value: data.modelName,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: modelNameOptions,
    },
    {
      dataKey: 'isEnabled',
      label: 'Enable Model',
      value: data.isEnabled,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: enabledOptions,
    },
    {
      dataKey: 'upperUpperThreshold',
      label: 'Upper Upper Threshold',
      value: data.upperUpperThreshold,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
      dependencies: [
        {
          name: 'modelName',
          has: DetectionModelName.BASIC_THRESHOLD,
        },
      ],
    },
    {
      dataKey: 'upperThreshold',
      label: 'Upper Threshold',
      value: data.upperThreshold,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
      dependencies: [
        {
          name: 'modelName',
          has: DetectionModelName.BASIC_THRESHOLD,
        },
      ],
    },
    {
      dataKey: 'lowerThreshold',
      label: 'Lower Threshold',
      value: data.lowerThreshold,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
      dependencies: [
        {
          name: 'modelName',
          has: DetectionModelName.BASIC_THRESHOLD,
        },
      ],
    },
    {
      dataKey: 'lowerLowerThreshold',
      label: 'Lower Lower Threshold',
      value: data.lowerLowerThreshold,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
      dependencies: [
        {
          name: 'modelName',
          has: DetectionModelName.BASIC_THRESHOLD,
        },
      ],
    },
  ]
}

export const DetectionModelDataFromFormFields = (
  formFields: FormField[]
): DetectionModel => {
  const modelName =
    (getStringFieldByDataKey(formFields, 'modelName') as DetectionModelName) ??
    ''
  const isEnabled = getStringFieldByDataKey(formFields, 'isEnabled') === 'yes'

  const lowerThreshold = getNumberFieldByDataKey(formFields, 'lowerThreshold')
  const lowerLowerThreshold = getNumberFieldByDataKey(
    formFields,
    'lowerLowerThreshold'
  )
  const upperThreshold = getNumberFieldByDataKey(formFields, 'upperThreshold')
  const upperUpperThreshold = getNumberFieldByDataKey(
    formFields,
    'upperUpperThreshold'
  )
  const modelParameters: DetectionModel['model_parameters'] = {}
  if (lowerThreshold !== undefined) {
    modelParameters.lower_threshold = lowerThreshold
  }
  if (lowerLowerThreshold !== undefined) {
    modelParameters.lower_lower_threshold = lowerLowerThreshold
  }
  if (upperThreshold !== undefined) {
    modelParameters.upper_threshold = upperThreshold
  }
  if (upperUpperThreshold !== undefined) {
    modelParameters.upper_upper_threshold = upperUpperThreshold
  }

  return {
    model_name: modelName,
    is_enabled: isEnabled,
    model_parameters: modelParameters,
  }
}
